import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, InlineLoading, InlineNotification, Stack, TextArea, TextInputSkeleton } from '@carbon/react';
import { SeoEditor, StorefrontSeoValues } from 'src/components/Storefront/SeoEditor';
import { ThemeEditor, StorefrontThemeValues } from 'src/components/Storefront/ThemeEditor';
import { useClearSavedState } from 'src/hooks/useClearSavedState';
import { useStorefront, useUpdateStorefrontMutation } from 'src/hooks/useStorefront';
import { StorefrontSeoDto, StorefrontThemeDto } from 'src/services/apis/types';

/**
 * A cleared image is sent as `null`, NOT dropped.
 *
 * `ImageField` calls back with `null` when the uploader is cleared. Mapping that to `undefined`
 * made `JSON.stringify` remove the key, and the server's update merges the patch onto the stored
 * theme (`{...defaultTheme(), ...storefront.theme, ...dto.theme}`) — an absent key means "leave it
 * alone". So clearing the logo said "Saved", changed nothing, and the old logo was still live on
 * the public site after a reload. `null` is what actually clears it: the DTO's `@IsOptional()`
 * skips validation for null and the merge writes it through.
 *
 * The cast is the narrow lie: the API's `StorefrontThemeDto` declares these `nullable: true`, but
 * swagger-typescript drops nullability when it generates `types.ts`, so the generated type says
 * `string | undefined`. Regenerating would not change that — the generator, not the schema, is
 * what loses it.
 */
function toThemePayload(values: StorefrontThemeValues): StorefrontThemeDto {
  return {
    primaryColor: values.primaryColor,
    accentColor: values.accentColor,
    fontPair: values.fontPair,
    logoFileId: values.logoFileId,
    heroFileId: values.heroFileId,
  } as StorefrontThemeDto;
}

/** The cap the API enforces, restated so the field blocks before the request rather than after. */
const CUSTOM_CSS_MAX = 20_000;

/**
 * Owner CSS, applied after the design system.
 *
 * `<` is rejected here because the API rejects it: it is not valid CSS outside a string, and its
 * absence is what makes a `</style>` breakout impossible in the `<style>` element the storefront
 * emits. Catching it in the form turns a 422 into a field error, but the server's check is the
 * one that matters — this one is a courtesy.
 *
 * Deliberately a plain `TextArea` and not a code editor: a syntax-highlighted editor is a large
 * dependency for a field most restaurants will never open, and it would imply a validation this
 * does not perform. Broken CSS is recoverable — the preview link shows the result before anyone
 * else sees it, and clearing the box undoes it.
 */
export const CustomCssEditor = React.memo(({ value, onSave }: { value: string; onSave: (css: string) => void }) => {
  const { t } = useTranslation();
  const [css, setCss] = useState(value);
  const invalid = css.includes('<') || css.length > CUSTOM_CSS_MAX;

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onSave(css);
    },
    [onSave, css]
  );

  return (
    <Form onSubmit={handleSubmit}>
      <Stack gap={5}>
        <TextArea
          id="customCss"
          name="customCss"
          labelText={t('storefront.appearance.customCss')}
          helperText={t('storefront.appearance.customCssHint')}
          rows={10}
          value={css}
          invalid={invalid}
          invalidText={
            css.includes('<') ? t('storefront.appearance.customCssAngle') : t('storefront.appearance.customCssTooLong')
          }
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setCss(event.target.value)}
        />
        <div>
          <Button type="submit" disabled={invalid}>
            {t('storefront.actions.save')}
          </Button>
        </div>
      </Stack>
    </Form>
  );
});

CustomCssEditor.displayName = 'CustomCssEditor';

/**
 * `title`/`description` follow `toThemePayload`'s `?? undefined` convention — an omitted field
 * leaves the stored value alone (`storefront.service.ts:124-131`), and those two are never
 * cleared by this screen (they're plain, non-nullable strings on `StorefrontSeoDto`).
 *
 * `ogImageFileId` cannot use that convention: it is the one field this screen *does* clear, and
 * the server distinguishes "omitted" from "explicit null" — a clear must reach it as `null`, not
 * be dropped. The generated `StorefrontSeoDto.ogImageFileId` lost the server DTO's
 * `nullable: true` in codegen (`storefrontEditor.dto.ts:91-95` declares `string | null`;
 * `types.ts` generated `ogImageFileId?: string`), so passing `null` needs a narrow local cast
 * rather than a widened type. `JSON.stringify` keeps a `null`-valued key and drops an
 * `undefined`-valued one, so this is also what makes the wire payload actually carry the clear.
 */
function toSeoPayload(values: StorefrontSeoValues): StorefrontSeoDto {
  return {
    title: values.title ?? undefined,
    description: values.description ?? undefined,
    ogImageFileId: values.ogImageFileId as string | undefined,
  };
}

/**
 * Plan 01's `view()` always returns a defaulted theme, so a missing one means the request has
 * not landed yet — the page never invents defaults of its own. `theme` is loosely typed on the
 * generated entity (`{ [x: string]: any }`), so it is narrowed to the shape `ThemeEditor`
 * declares rather than left as `any`.
 */
const StorefrontAppearance = React.memo(() => {
  const { t } = useTranslation();
  const { data: storefront, isLoading } = useStorefront();
  const {
    mutate: updateStorefront,
    isLoading: saving,
    isSuccess: saved,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateStorefrontMutation();

  useClearSavedState(saved, resetUpdate);

  return (
    <Stack gap={6}>
      {isLoading && <TextInputSkeleton />}
      {!isLoading && storefront && (
        <>
          <ThemeEditor
            theme={storefront.theme as StorefrontThemeValues}
            onSave={(theme) => updateStorefront({ theme: toThemePayload(theme) })}
            saving={saving}
          />
          <SeoEditor
            seo={storefront.seo as StorefrontSeoValues}
            onSave={(seo) => updateStorefront({ seo: toSeoPayload(seo) })}
            saving={saving}
          />
        </>
      )}
      {!isLoading && storefront && (
        <CustomCssEditor
          value={(storefront.customCss as string | null) ?? ''}
          onSave={(customCss) => updateStorefront({ customCss } as never)}
        />
      )}
      {saving || saved ? (
        <InlineLoading
          description={saved ? t('common.saved') : `${t('common.saving')}...`}
          status={saved ? 'finished' : 'active'}
        />
      ) : null}
      {updateError ? <InlineNotification kind="error" subtitle={t('messages.somethingWentWrong')} /> : null}
    </Stack>
  );
});

export default StorefrontAppearance;
